import { createSlice } from '@reduxjs/toolkit';
import { paymentApi } from '../apis/endpoints/paymentApi';

const paymentSlice = createSlice({
  name: 'payment',
  initialState: {
    clientSecret: null,
    paymentMethods: [],
    loading: false,
    error: null,
    currentOrder: null,
    stripe: null, // Will hold the Stripe instance
    elements: null, // Will hold Stripe Elements instance
    // Stripe Connect state
    stripeAccount: null,
    accountStatus: {
      detailsSubmitted: false,
      chargesEnabled: false,
    },
    accountSession: null,
    accountBalance: {
      available: [],
      pending: [],
    },
    accountTransactions: []
  },
  reducers: {
    setStripe: (state, action) => {
      state.stripe = action.payload;
    },
    setElements: (state, action) => {
      state.elements = action.payload;
    },
    clearPaymentState: (state) => {
      state.clientSecret = null;
      state.currentOrder = null;
      state.error = null;
    },
    setCurrentOrder: (state, action) => {
      state.currentOrder = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearStripeAccount: (state) => {
      state.stripeAccount = null;
      state.accountStatus = {
        detailsSubmitted: false,
        chargesEnabled: false,
      };
      state.accountSession = null;
      state.accountBalance = {
        available: [],
        pending: []
      };
      state.accountTransactions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle paymentApi results
      .addMatcher(
        paymentApi.endpoints.createPaymentIntent.matchFulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.clientSecret = payload.clientSecret;
        }
      )
      .addMatcher(
        paymentApi.endpoints.createPaymentIntent.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        paymentApi.endpoints.createPaymentIntent.matchRejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload?.data?.message || 'Failed to create payment intent';
        }
      )
      .addMatcher(
        paymentApi.endpoints.confirmPayment.matchFulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.currentOrder = payload;
        }
      )
      .addMatcher(
        paymentApi.endpoints.confirmPayment.matchPending,
        (state) => {
          state.loading = true;
        }
      )
      .addMatcher(
        paymentApi.endpoints.confirmPayment.matchRejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload?.data?.message || 'Failed to confirm payment';
        }
      )
      .addMatcher(
        paymentApi.endpoints.fetchPaymentMethods.matchFulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.paymentMethods = payload;
        }
      )
      .addMatcher(
        paymentApi.endpoints.fetchPaymentMethods.matchPending,
        (state) => {
          state.loading = true;
        }
      )
      .addMatcher(
        paymentApi.endpoints.fetchPaymentMethods.matchRejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload?.data?.message || 'Failed to fetch payment methods';
        }
      )
      .addMatcher(
        paymentApi.endpoints.createStripeAccount.matchFulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.stripeAccount = payload.accountId;
        }
      )
      .addMatcher(
        paymentApi.endpoints.createStripeAccount.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        paymentApi.endpoints.createStripeAccount.matchRejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload?.data?.message || 'Failed to create Stripe account';
        }
      )
      .addMatcher(
        paymentApi.endpoints.createAccountSession.matchFulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.accountSession = payload.client_secret;
        }
      )
      .addMatcher(
        paymentApi.endpoints.createAccountSession.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        paymentApi.endpoints.createAccountSession.matchRejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload?.data?.message || 'Failed to create account session';
        }
      )
      .addMatcher(
        paymentApi.endpoints.getAccountStatus.matchFulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.accountStatus = payload.status;
        }
      )
      .addMatcher(
        paymentApi.endpoints.getAccountStatus.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        paymentApi.endpoints.getAccountStatus.matchRejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload?.data?.message || 'Failed to get account status';
        }
      )
      .addMatcher(
        paymentApi.endpoints.getAccountBalance.matchFulfilled,
        (state, { payload }) => {
          state.loading = false;
          state.accountBalance = payload.balance;
          state.accountTransactions = payload.transactions || [];
        }
      )
      .addMatcher(
        paymentApi.endpoints.getAccountBalance.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        paymentApi.endpoints.getAccountBalance.matchRejected,
        (state, { payload }) => {
          state.loading = false;
          state.error = payload?.data?.message || 'Failed to get account balance';
        }
      );
  },
});

export const {
  setStripe,
  setElements,
  clearPaymentState,
  setCurrentOrder,
  clearError,
  clearStripeAccount,
} = paymentSlice.actions;

export default paymentSlice.reducer; 